/**
 * Storefront display-currency context.
 *
 * Customer-facing prices are stored + computed in the TENANT'S BOOKS BASE
 * (``Organization.base_currency`` — koruna for every tenant so far, but the
 * engine has not assumed it since KAN-542); this is the DISPLAY layer. The tenant's chosen display currency and
 * the latest ČNB rate come from the public branding endpoint
 * (``checkout_currency`` / ``checkout_fx_rate_to_czk``). ``CurrencyProvider``
 * (mounted once at the storefront shell) reads branding; ``useDisplayMoney``
 * formats a CZK amount into that currency via the shared ``formatMoney`` helper.
 *
 * The context defaults to CZK identity, so a component rendered without the
 * provider (or before branding resolves, or under tests) simply shows CZK — it
 * never crashes and never needs its own QueryClient.
 *
 * SCOPE: this is for PRE-checkout LIVE pricing (configurator preview + cart),
 * which converts at the current rate. A PLACED order / invoice / quote must
 * display from its OWN frozen ``currency`` + ``fx_rate_to_czk`` snapshot (so a
 * paid invoice never changes when the rate moves) — do NOT use this for those.
 */
import {
  createContext,
  useContext,
  useMemo,
  type JSX,
  type ReactNode,
} from "react";

import { useTenantBranding } from "@/api/queries/branding";
import { formatMoney } from "@/lib/utils";

interface DisplayCurrency {
  currency: string;
  fxRateToCzk: number;
}

const CurrencyContext = createContext<DisplayCurrency>({
  currency: "CZK",
  fxRateToCzk: 1,
});

export function CurrencyProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const branding = useTenantBranding();
  const value = useMemo<DisplayCurrency>(
    () => ({
      currency: branding.data?.checkout_currency ?? "CZK",
      fxRateToCzk: branding.data?.checkout_fx_rate_to_czk ?? 1,
    }),
    [branding.data?.checkout_currency, branding.data?.checkout_fx_rate_to_czk],
  );
  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

/**
 * Format a CZK amount in the tenant's display currency. Pass the ``locale`` the
 * component already carries (the prop used for the old ``formatCzk(x, locale)``).
 * Depends only on context (CZK-default), so it is safe in any render tree.
 */
export function useDisplayMoney(locale?: string): {
  currency: string;
  fxRateToCzk: number;
  format: (amountCzk: number) => string;
  /** KAN-2189 — cena za kus na dvě desetinná místa, aby kus × počet seděl. */
  formatUnit: (amount: number) => string;
} {
  const { currency, fxRateToCzk } = useContext(CurrencyContext);
  const loc = locale || "cs-CZ";
  const { format, formatUnit } = useMemo(() => {
    const make = (unitPrice: boolean) => (amountCzk: number) =>
      formatMoney(amountCzk, currency, loc, fxRateToCzk, unitPrice);
    return { format: make(false), formatUnit: make(true) };
  }, [currency, fxRateToCzk, loc]);
  return { currency, fxRateToCzk, format, formatUnit };
}

/**
 * An amount written the way the given locale writes money, but with the
 * catalog's OWN symbol for the currency (or its code when it has none) —
 * the platform-admin „Měny" preview shows what a symbol edit will look like
 * before it is saved, so Intl's built-in symbol would show the wrong thing.
 *
 * ``fractionDigits`` comes from the API's read-only exponents (KAN-1983) —
 * never from user input; omitted = Intl's default for the code.
 */
export function formatAmountWithSymbol(
  amount: number,
  code: string,
  symbol: string | null | undefined,
  locale: string,
  fractionDigits?: number,
): string {
  const mark = symbol?.trim() || code;
  const digits =
    fractionDigits === undefined
      ? {}
      : {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        };
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      currencyDisplay: "code",
      ...digits,
    })
      .formatToParts(amount)
      .map((part) => (part.type === "currency" ? mark : part.value))
      .join("")
      .trim();
  } catch {
    // A half-typed code is not a currency Intl accepts.
    return `${new Intl.NumberFormat(locale, digits).format(amount)} ${mark}`;
  }
}

/** The currency's name in ``lang`` from Unicode CLDR — ``null`` when unknown. */
export function currencyDisplayName(lang: string, code: string): string | null {
  try {
    const name = new Intl.DisplayNames([lang], { type: "currency" }).of(code);
    return name && name !== code ? name : null;
  } catch {
    return null;
  }
}
